/**
 * ttStockTraderService — CRUD + read endpoints for TT Stock engines.
 *
 * Engine records live in algo-engines-prod alongside Alpaca + TT-options
 * engines; we filter by brokerType==='tt-stock'. Same access pattern as
 * optionsTraderService.
 */

const API_BASE = process.env.REACT_APP_API_URL
  || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

async function authFetch(path: string, token: string, options: RequestInit = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', ...options.headers },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}${text ? `: ${text}` : ''}`);
  }
  return res.json();
}

export type StockRisk = 'conservative' | 'moderate' | 'aggressive';
export type StockStrategy =
  | 'auto-directional'
  | 'momentum' | 'oversold-bounce' | 'breakout' | 'trend-following'
  | 'bearish-momentum' | 'overbought-reversal' | 'breakdown' | 'bearish-trend-following'
  | 'stock_signals';
export type StockSchedule = '15min' | '30min' | '60min' | 'daily' | '15min_ext' | '30min_ext' | '60min_ext';
export type UniversePreset = 'mega_cap_tech' | 'spdr_sectors' | 'broad_etfs' | 'custom' | 'all_signals';

export interface StockPolicy {
  policyId: string;
  strategyId: StockStrategy;
  riskAppetite: StockRisk;
  symbols: string[];
  universePreset?: UniversePreset;
  maxPerRun?: number;
  maxOpenTradesOverride?: number;
}

export interface StockEngine {
  engineId: string;
  name: string;
  status: 'active' | 'paused';
  brokerType: 'tt-stock';
  venue?: string;
  paperMode: boolean;
  schedule: StockSchedule;
  autoExecuteEnabled: boolean;
  tastytradeAccountNumber?: string;
  tastytradeConnectionId?: string;
  tastytradeConnectionLabel?: string;
  stockConfig: {
    regimeOverlay: boolean;
    startingEquity: number;
    extendedHours?: boolean;
    policies: StockPolicy[];
  };
  stockFillConfig?: {
    stepSizePct?: number;
    maxDeviationPct?: number;
    maxAgeHours?: number;
    preferLowerClose?: boolean;
  };
  createdAt: string;
  updatedAt?: string;
  lastRunAt?: string;
}

export interface CreateStockEngineRequest {
  name: string;
  schedule: StockSchedule;
  paperMode: boolean;
  regimeOverlay?: boolean;
  startingEquity?: number;
  extendedHours?: boolean;
  policies: Array<{
    policyId?: string;
    strategyId: StockStrategy;
    riskAppetite: StockRisk;
    symbols?: string[];
    universePreset?: UniversePreset;
    maxPerRun?: number;
    maxOpenTradesOverride?: number;
  }>;
  stockFillConfig?: {
    stepSizePct: number;
    maxDeviationPct: number;
    maxAgeHours: number;
    preferLowerClose: boolean;
  };
  connectionId?: string;
  accountNumber?: string;
  disclaimerAccepted?: boolean;
}

export async function getStockEngines(token: string): Promise<StockEngine[]> {
  const data = await authFetch('/algo/engines', token);
  return (data.engines || []).filter((e: any) => e.brokerType === 'tt-stock');
}

export async function createStockEngine(token: string, body: CreateStockEngineRequest): Promise<StockEngine> {
  const data = await authFetch('/tt-stock/engines', token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return data.engine;
}

export async function updateStockEngine(
  token: string,
  engineId: string,
  body: Partial<CreateStockEngineRequest & { status: 'active' | 'paused' }>,
): Promise<void> {
  await authFetch(`/tt-stock/engines/${engineId}`, token, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteStockEngine(token: string, engineId: string): Promise<void> {
  await authFetch(`/tt-stock/engines/${engineId}`, token, { method: 'DELETE' });
}

// ─── Trades + Audit ────────────────────────────────────────────────────────

export interface StockTrade {
  tradeId: string;
  engineId: string;
  userId: string;
  symbol: string;
  side: 'buy' | 'sell_short';
  direction: 'long' | 'short';
  qty: number;
  strategy: string;
  strategy_name?: string;
  venue: 'tt-stock';
  paperMode: boolean;
  assetClass: 'stocks';
  status: 'placing' | 'submitted' | 'open' | 'closed' | 'cancelled' | 'failed';
  filledPrice?: number;
  limitPrice?: number;
  suggestedStop?: number;
  suggestedTarget?: number;
  trailingStop?: number | null;
  highSinceEntry?: number;
  currentPrice?: number;
  unrealizedPnl?: number;
  exitPrice?: number;
  exitAt?: string;
  exitReason?: string;
  pnl?: number;
  ttOrderId?: string | null;
  base_score?: number;
  score?: number;
  regime_multiplier?: number;
  regime_adjustments?: Array<{ factor: string; value: number; reason: string }>;
  rationale?: string;
  entryAt?: string;
  filledAt?: string;
  placingAt?: string;
  failureReason?: string;
  entryEquity?: number;
  entryEquitySource?: 'paper_form' | 'snapshot' | 'tt_live_refresh' | 'live_no_tt_connection' | 'live_refresh_failed';
  entryNetLiq?: number;
  entryAccountNumber?: string | null;
}

export async function getStockTrades(token: string, params?: { engineId?: string; status?: string; limit?: number }): Promise<StockTrade[]> {
  const q = new URLSearchParams({ source: 'trades' });
  if (params?.engineId) q.set('engineId', params.engineId);
  if (params?.status) q.set('status', params.status);
  q.set('limit', String(params?.limit || 200));
  const data = await authFetch(`/algo/signals?${q.toString()}`, token);
  return (data.signals || []).filter((t: any) => t.venue === 'tt-stock');
}

export interface AuditEvent {
  userId: string;
  eventId: string;
  engineId?: string;
  eventType: string;
  timestamp: string;
  symbol?: string;
  tradeId?: string;
  details?: any;
}

export async function getStockAuditEvents(
  token: string,
  opts?: { engineId?: string; eventType?: string; limit?: number },
): Promise<AuditEvent[]> {
  const q = new URLSearchParams({ limit: String(opts?.limit || 200) });
  if (opts?.engineId) q.set('engineId', opts.engineId);
  if (opts?.eventType) q.set('eventType', opts.eventType);
  const data = await authFetch(`/algo/audit?${q.toString()}`, token);
  return (data.events || data.items || []) as AuditEvent[];
}
